import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell } from "recharts";

const MODES = [
  { id: "month_vs_month", label: "Month vs Last Month" },
  { id: "month_vs_last_year", label: "Month vs Same Month Last Year" },
  { id: "quarter", label: "This Quarter vs Last Quarter" },
  { id: "year", label: "This Year vs Last Year" },
  { id: "by_agent", label: "By Agent" },
  { id: "by_state", label: "By State" },
  { id: "by_town", label: "By Town" },
  { id: "by_customer", label: "By Customer" },
  { id: "by_product", label: "By Product" },
];

export default function SalesPerformance() {
  const [mode, setMode] = useState("month_vs_month");
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [ba, setBa] = useState(null);
  const [window, setWindow] = useState(30);

  useEffect(() => {
    api.get("/sales/comparison", { params: { mode, month } }).then(r => setData(r.data));
  }, [mode, month]);
  useEffect(() => {
    api.get("/sales/before-after", { params: { window } }).then(r => setBa(r.data));
  }, [window]);

  const chartData = data ? data.labels.map((l, i) => ({ name: l, total: data.values[i] })) : [];

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">Sales Performance</h1>
          <p className="text-slate-600 mt-1">Compare sales across time, geography, agents & products</p>
        </div>
        <div className="flex gap-2">
          <select value={mode} onChange={(e) => setMode(e.target.value)} className="h-11 px-3 border border-slate-300 rounded-lg" data-testid="sales-mode">
            {MODES.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-11 px-3 border border-slate-300 rounded-lg" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-lg font-heading font-semibold mb-4">{MODES.find(m => m.id === mode)?.label}</h3>
        {chartData.length === 0 ? <div className="text-sm text-slate-500">No data.</div> :
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={70} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => money(v)} />
              <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                {chartData.map((_, i) => <Cell key={i} fill={i % 2 === 0 ? "#003DA5" : "#001F54"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-heading font-semibold">Sales Before vs After Visit</h3>
            <p className="text-xs text-slate-500">Correlational — sales are not directly attributed to visits.</p>
          </div>
          <select value={window} onChange={(e) => setWindow(Number(e.target.value))} className="h-11 px-3 border border-slate-300 rounded-lg" data-testid="ba-window">
            <option value={7}>7 days window</option>
            <option value={14}>14 days window</option>
            <option value={30}>30 days window</option>
            <option value={60}>Calendar month (approx.)</option>
          </select>
        </div>
        {!ba ? <div>Loading…</div> :
          <>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-slate-50 rounded-lg p-4"><div className="text-xs uppercase text-slate-500 font-semibold">Total Before</div><div className="kpi-value text-3xl">{money(ba.total_before)}</div></div>
              <div className="bg-slate-50 rounded-lg p-4"><div className="text-xs uppercase text-slate-500 font-semibold">Total After</div><div className="kpi-value text-3xl text-[#003DA5]">{money(ba.total_after)}</div></div>
            </div>
            <div className="overflow-x-auto max-h-72">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500"><tr><th className="text-left py-2">Visit Date</th><th className="text-left">Customer</th><th className="text-right">Before</th><th className="text-right">After</th></tr></thead>
                <tbody>
                  {ba.rows.slice(0, 50).map(r => <tr key={r.visit_id} className="border-t"><td className="py-1.5">{r.visit_date}</td><td>{r.customer_name}</td><td className="text-right">{money(r.before)}</td><td className="text-right text-[#003DA5] font-semibold">{money(r.after)}</td></tr>)}
                </tbody>
              </table>
            </div>
          </>
        }
      </div>
    </div>
  );
}
